import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
 } from "react-router-dom";
import React, {Fragment, useCallback, useState, useEffect} from "react";
import axios from "axios";
import FileBrowser from "./views/FileBrowser";
import Login from "./views/Login";
import AccountApprovals from './views/AccountApprovals';
import AssociationImport from './views/AssociationImport';
import ResendVerification from './views/ResendVerification';
import RegUser from "./views/RegUser";
import MyPage from "./views/MyPage";
import MyWorkouts from "./views/MyWorkouts";
import CreateSession from "./views/CreateSession";
import ManageSession from "./views/ManageSession"
import RunSession from "./views/RunSession";
import Banner from "./components/Banner";
import NavBar from "./components/NavBar";
import AdminDashboard from "./views/AdminDashboard";
import UserSettings from "./views/UserSettings";
import ModifyUser from "./views/ModifyUser";
import DeleteUser from "./views/DeleteUser";
import LoginLog from "./views/LoginLog";
import VideoMetadataAdmin from "./views/VideoMetadataAdmin";
import UserManagement from "./views/UserManagement";
import AdminAnalytics from "./views/AdminAnalytics";
import AuditTrail from "./views/AuditTrail";
import EmailStatus from "./views/EmailStatus";
import Help from "./views/Help";
import FloatingHelpAssistant from "./components/FloatingHelpAssistant";
import GroupManagement from "./views/GroupManagement";
import MyInvitations from "./views/MyInvitations";
import ForgotPassword from "./views/ForgotPassword";
import ResetPassword from "./views/ResetPassword";
import VerifyEmail from "./views/VerifyEmail";
import Notifications from "./views/Notifications";
import AdminOperations from "./views/AdminOperations";
import AdminGroups from "./views/AdminGroups";
import { clearStoredAuth, getCurrentUsername, getTokenExpiryTime, isTokenExpired } from "./utils/auth";

import { GuideProvider, GuidePanel } from "./guides/GuideProvider";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL
const DARK_MODE_STORAGE_KEY = "darkMode";

function App() {
  const [authenticatedUserRole, setAuthenticatedUserRole] = useState(0);
  const [authChecking, setAuthChecking] = useState(() => Boolean(localStorage.getItem("token")));
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem(DARK_MODE_STORAGE_KEY) === "true");
  
  const [exercise, setExercise] = useState({
    session: "",
    title: "",
    description: "",
    videoPath: "Ingen video vald",
  });

  const setAuth = useCallback((role) => {
    try {
      const roleInt = parseInt(role, 10);
      setAuthenticatedUserRole(Number.isNaN(roleInt) ? 0 : roleInt);
    }
    catch (err) {
      setAuthenticatedUserRole(0);
    }
  }, []);

  const logout = useCallback(() => {
    clearStoredAuth();
    setAuthenticatedUserRole(0);
  }, []);

  const isAuth = useCallback(async () => {
    // If localStorage is empty just exit quietly
    if (!localStorage.getItem("token")) {
      setAuthenticatedUserRole(0);
      return;
    }

    if (isTokenExpired()) {
      logout();
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/is-verified`, {
        method: "GET",
        headers: {token: localStorage.token}
      });

      if (!response.ok) {
        logout();
        return;
      }

      const role = await response.json();

      setAuth(role);
    } catch (err) {
      logout();
    }
  }, [logout, setAuth]);

  useEffect(() => {
    isAuth().finally(() => setAuthChecking(false));
  }, [isAuth]);

  useEffect(() => {
    if (!authenticatedUserRole) {
      return undefined;
    }

    const logoutIfExpired = () => {
      if (isTokenExpired()) {
        logout();
      }
    };

    const expiresAt = getTokenExpiryTime();
    if (!expiresAt) {
      logout();
      return undefined;
    }

    const timeoutId = window.setTimeout(logout, Math.max(expiresAt - Date.now(), 0));

    window.addEventListener("focus", logoutIfExpired);
    document.addEventListener("visibilitychange", logoutIfExpired);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("focus", logoutIfExpired);
      document.removeEventListener("visibilitychange", logoutIfExpired);
    };
  }, [authenticatedUserRole, logout]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const { status, data } = error.response || {};
        if (status === 401 && typeof data?.code === "string" && data.code.startsWith("AUTH_")) {
          logout();
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  useEffect(() => {
    document.body.classList.toggle("theme-dark", darkMode);
    localStorage.setItem(DARK_MODE_STORAGE_KEY, String(darkMode));
  }, [darkMode]);

  if (authChecking) return <div role="status" className="p-6 text-center">Kontrollerar inloggningen…</div>;

  return (
    <Fragment>
      <div className="flex h-screen flex-col">
        <Router>
        <GuideProvider userRole={authenticatedUserRole}>
        <Banner loggedIn={authenticatedUserRole}/>
        <GuidePanel />
          <div className="min-h-0 flex-1 overflow-y-auto pb-44 sm:pb-24">
          <Routes>
            <Route path="/" element={authenticatedUserRole ? (<MyPage userRole={authenticatedUserRole} logout={logout}/>) : (<Navigate replace to="/login" />)}/>
            <Route path="/login" element={!authenticatedUserRole ? (<Login setAuth={setAuth}/>) : (<Navigate replace to="/" />)} />
            <Route path="/register" element={!authenticatedUserRole ? <RegUser /> : <Navigate to="/" replace />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
            <Route path="/account-approvals" element={[1,2].includes(authenticatedUserRole) ? <AccountApprovals /> : <Navigate to="/login" replace />} />
            <Route path="/association-import" element={authenticatedUserRole === 1 ? <AssociationImport /> : <Navigate to="/login" replace />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/admin-dashboard" element={(authenticatedUserRole === 1 || authenticatedUserRole === 2) ? (<AdminDashboard userRole={authenticatedUserRole}/>) : (<Navigate replace to="/mypage" />)}/>
            <Route path="/reg-user" element={(authenticatedUserRole === 1 || authenticatedUserRole === 2) ? (<RegUser userRole={authenticatedUserRole}/>) : (<Navigate replace to="/admin-dashboard" />)}/>
            <Route path="/modify-user" element={(authenticatedUserRole === 1) ? (<ModifyUser userRole={authenticatedUserRole}/>) : (<Navigate replace to="/admin-dashboard" />)}/>
            <Route path="/delete-user" element={(authenticatedUserRole === 1) ? (<DeleteUser userRole={authenticatedUserRole}/>) : (<Navigate replace to="/admin-dashboard" />)}/>
            <Route path="/login-log" element={(authenticatedUserRole === 1 || authenticatedUserRole === 2) ? (<LoginLog userRole={authenticatedUserRole}/>) : (<Navigate replace to="/admin-dashboard" />)}/>
            <Route path="/video-metadata" element={(authenticatedUserRole === 1 || authenticatedUserRole === 2) ? (<VideoMetadataAdmin userRole={authenticatedUserRole}/>) : (<Navigate replace to="/admin-dashboard" />)}/>
            <Route path="/user-management" element={([1, 2, 4].includes(authenticatedUserRole)) ? (<UserManagement userRole={authenticatedUserRole}/>) : (<Navigate replace to="/admin-dashboard" />)}/>
            <Route path="/admin-analytics" element={(authenticatedUserRole === 1 || authenticatedUserRole === 2) ? (<AdminAnalytics userRole={authenticatedUserRole}/>) : (<Navigate replace to="/admin-dashboard" />)}/>
            <Route path="/audit-trail" element={(authenticatedUserRole === 1 || authenticatedUserRole === 2) ? (<AuditTrail userRole={authenticatedUserRole}/>) : (<Navigate replace to="/admin-dashboard" />)}/>
            <Route path="/email-status" element={(authenticatedUserRole === 1 || authenticatedUserRole === 2) ? (<EmailStatus userRole={authenticatedUserRole}/>) : (<Navigate replace to="/admin-dashboard" />)}/>
            <Route path="/admin-operations" element={(authenticatedUserRole === 1 || authenticatedUserRole === 2) ? (<AdminOperations />) : (<Navigate replace to="/admin-dashboard" />)}/>
            <Route path="/admin-groups" element={(authenticatedUserRole === 1 || authenticatedUserRole === 2) ? (<AdminGroups />) : (<Navigate replace to="/admin-dashboard" />)}/>
            <Route path="/mypage" element={authenticatedUserRole ? (<MyPage userRole={authenticatedUserRole} logout={logout}/>) : (<Navigate replace to="/login" />)}/>
            <Route path="/videos" element={authenticatedUserRole ? (<FileBrowser userRole={authenticatedUserRole} exercise={exercise} setExercise={setExercise}/>) : (<Navigate replace to="/login" />)}/>
            <Route path="/my-workouts" element={authenticatedUserRole ? (<MyWorkouts userRole={authenticatedUserRole} />) : (<Navigate replace to="/login" />)}/>
            <Route path="/my-groups" element={[1, 2, 4, 5].includes(authenticatedUserRole) ? (<GroupManagement userRole={authenticatedUserRole} />) : (<Navigate replace to="/mypage" />)}/>
            <Route path="/invitations" element={authenticatedUserRole ? (<MyInvitations userRole={authenticatedUserRole} />) : (<Navigate replace to="/login" />)}/>
            <Route path="/notifications" element={authenticatedUserRole ? (<Notifications />) : (<Navigate replace to="/login" />)}/>
            <Route path="/createsession" element={authenticatedUserRole ? (<CreateSession userRole={authenticatedUserRole} exercise={exercise} setExercise={setExercise} />) : (<Navigate replace to="/login" />)}/>
            <Route path="/managesession" element={authenticatedUserRole ? (<ManageSession userRole={authenticatedUserRole} exercise={exercise} setExercise={setExercise} />) : (<Navigate replace to="/login" />)}/>
            <Route path="/run-session" element={authenticatedUserRole ? (<RunSession userRole={authenticatedUserRole} />) : (<Navigate replace to="/login" />)}/>
            <Route path="/usersettings" element={authenticatedUserRole ? (<UserSettings userRole={authenticatedUserRole} darkMode={darkMode} setDarkMode={setDarkMode} />) : (<Navigate replace to="/login" />)}/>
            <Route path="/help" element={<Help userRole={authenticatedUserRole} />} />
            <Route path="/hjalp" element={<Help userRole={authenticatedUserRole} />} />
          </Routes>
          </div>
          { Boolean(authenticatedUserRole) && <NavBar userRole={authenticatedUserRole}/> }
          <FloatingHelpAssistant userRole={authenticatedUserRole} sessionKey={getCurrentUsername()} />
        </GuideProvider>
        </Router>
      </div>
    </Fragment>
  );
}

export default App;
